import { useEffect, useRef, type ReactNode } from 'react';
import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import { Logo } from '@practium/ui';
import styles from './SelectionModal.module.css';

export interface SelectionModalProps {
	isOpen: boolean;
	title: string;
	searchPlaceholder?: string;
	searchValue: string;
	onSearchChange: (value: string) => void;
	onClose: () => void;
	children: ReactNode;
	footer?: ReactNode;
}

export const SelectionModal = ({
	isOpen,
	title,
	searchPlaceholder = 'Buscar...',
	searchValue,
	onSearchChange,
	onClose,
	children,
	footer,
}: SelectionModalProps) => {
	const searchRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (isOpen) {
			setTimeout(() => searchRef.current?.focus(), 100);
		}
	}, [isOpen]);

	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && isOpen) {
				onClose();
			}
		};
		document.addEventListener('keydown', handleEscape);
		return () => document.removeEventListener('keydown', handleEscape);
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	return (
		<div className={styles.overlay} onClick={onClose}>
			<div className={styles.modal} onClick={(e) => e.stopPropagation()}>
				<div className={styles.header}>
					<div className={styles.headerTop}>
						<Logo className={styles.logo} />
						<button type="button" className={styles.closeButton} onClick={onClose}>
							<Close fontSize="small" />
						</button>
					</div>
					<div className={styles.headerText}>
						<h2 className={styles.title}>{title}</h2>
					</div>
				</div>

				<div className={styles.searchWrapper}>
					<Search className={styles.searchIcon} fontSize="small" />
					<input
						ref={searchRef}
						type="text"
						className={styles.searchInput}
						placeholder={searchPlaceholder}
						value={searchValue}
						onChange={(e) => onSearchChange(e.target.value)}
					/>
				</div>

				<div className={styles.body}>{children}</div>

				{footer && <div className={styles.footer}>{footer}</div>}
			</div>
		</div>
	);
};
